import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../utils/apiConfig";
import SubPageLayout from "../../../components/shared/SubPageLayout";
import Modal from "../../../components/shared/Modal";
import {
  HealthScore,
  RiskExposureChart,
  generateRiskScore,
} from "../../../components/domains/advisor-portal/HealthAndRiskMetricsDetails";
import { NavigationIcons } from "../../../components/shared/NavigationIcons";
import Card from "../../../components/shared/Card";
import RiskProfileCard from "../../../components/domains/advisor-portal/RiskProfileCard";
import FinancialHealthCard from "../../../components/domains/advisor-portal/FinancialHealthCard";
import TaskRecommendations from "./TaskRecommendations";
import {
  getScoreGradient,
  buttonGradients,
  themeColors,
  cardStyles,
} from "../../../utils/colorUtils";
import CallConfirmationModal from "../../../components/domains/advisor-portal/CallConfirmationModal";
import EmailComposerModal from "../../../components/domains/advisor-portal/EmailComposerModal";
import NewMessageModal from "../../../components/domains/advisor-portal/NewMessageModal";
import ScheduleModal from "../../../components/domains/advisor-portal/ScheduleModal";
import { useTheme } from '../../../contexts/ThemeContext';

import logger from '../../../utils/logger';

// Icon components
const PhoneIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const EmailIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
  </svg>
);

const MessageIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
      clipRule="evenodd"
    />
  </svg>
);

// Tailwind utility classes
const cardClass =
  "bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-6 border border-gray-100 dark:border-gray-700";
const hoverUpClass =
  "transition-transform duration-200 hover:translate-y-[-2px]";
const buttonClass =
  "px-4 py-2 rounded-md font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:ring-offset-gray-800";
const primaryButtonClass = `${buttonClass} ${buttonGradients.primary} text-white`;
const secondaryButtonClass = `${buttonClass} bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600`;
const cardHeaderClass = "flex items-center mb-4";
const cardTitleClass = "text-lg font-semibold text-gray-900 dark:text-white";

// Loading spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-4">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
  </div>
);

// Utility functions
const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "";
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age;
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  const cleaned = phoneNumber.replace(/\D/g, "");
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
  return phoneNumber;
};

// Navigation icon progress and styling
const iconProgress = {
  Communications: { available: 95, total: 100 },
  Documents: { available: 85, total: 100 },
  Calculators: { available: 32, total: 46 },
  Relationship: { available: 45, total: 100 },
  Analysis: { available: 88, total: 100 },
  Investments: { available: 100, total: 100 },
  "Tax Planning": { available: 15, total: 100 },
  Banking: { available: 5, total: 100 },
  "Estate Planning": { available: 25, total: 100 },
  Insurance: { available: 10, total: 100 },
};

const calculateGradient = (available, total) => {
  const percentage = (available / total) * 100;
  if (percentage >= 90)
    return {
      background: "from-emerald-50 via-green-50 to-white",
      progress: "bg-emerald-500",
      hover: "group-hover:bg-emerald-600",
    };
  if (percentage >= 75)
    return {
      background: "from-green-50 via-lime-50 to-white",
      progress: "bg-green-500",
      hover: "group-hover:bg-green-600",
    };
  if (percentage >= 60)
    return {
      background: "from-lime-50 via-yellow-50 to-white",
      progress: "bg-lime-500",
      hover: "group-hover:bg-lime-600",
    };
  if (percentage >= 45)
    return {
      background: "from-yellow-50 via-yellow-50 to-white",
      progress: "bg-yellow-500",
      hover: "group-hover:bg-yellow-600",
    };
  if (percentage >= 30)
    return {
      background: "from-orange-50 via-yellow-50 to-white",
      progress: "bg-orange-500",
      hover: "group-hover:bg-orange-600",
    };
  if (percentage >= 15)
    return {
      background: "from-red-50 via-orange-50 to-white",
      progress: "bg-red-500",
      hover: "group-hover:bg-red-600",
    };
  return {
    background: "from-red-100 via-red-50 to-white",
    progress: "bg-red-600",
    hover: "group-hover:bg-red-700",
  };
};

// Header action buttons component
const HeaderActionButtons = ({
  showAnnualReviewDropdown,
  setShowAnnualReviewDropdown,
  showSettingsMenu,
  setShowSettingsMenu,
  loadingVersions,
  annualReviewVersions,
  handleViewAnnualReview,
  handleReassignClick,
  handleRemoveClick,
  dropdownRef,
  settingsRef,
}) => (
  <div className="flex items-center space-x-4">
    <div className="relative" ref={dropdownRef}>
      <button
        className={primaryButtonClass}
        onClick={(e) => {
          e.stopPropagation();
          setShowAnnualReviewDropdown(!showAnnualReviewDropdown);
          setShowSettingsMenu(false);
        }}
        aria-expanded={showAnnualReviewDropdown}
        aria-haspopup="true"
      >
        Annual Review Profile
      </button>
      <div
        className={`absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg z-50 ${showAnnualReviewDropdown ? "block" : "hidden"}`}
      >
        {loadingVersions ? (
          <LoadingSpinner />
        ) : Object.keys(annualReviewVersions).length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-700 border-b border-gray-100">
            No annual review profiles available
          </div>
        ) : (
          <div className="py-1">
            {Object.entries(annualReviewVersions).map(([year, versions]) => (
              <div key={year}>
                <div className="px-4 py-2 text-sm text-gray-700 font-semibold bg-gray-50 border-b border-gray-100">
                  {year}
                </div>
                {versions.map((version) => (
                  <button
                    key={`${year}-${version}`}
                    onClick={() => {
                      handleViewAnnualReview(year, version);
                      setShowAnnualReviewDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                  >
                    Version {version}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    <div className="relative" ref={settingsRef}>
      <button
        className={`${secondaryButtonClass} p-2`}
        onClick={(e) => {
          e.stopPropagation();
          setShowSettingsMenu(!showSettingsMenu);
          setShowAnnualReviewDropdown(false);
        }}
      >
        <svg
          className="w-6 h-6 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>
      {showSettingsMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50">
          <button
            onClick={handleReassignClick}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
          >
            Reassign Household
          </button>
          <button
            onClick={handleRemoveClick}
            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
          >
            Remove Household
          </button>
        </div>
      )}
    </div>
  </div>
);

// Update the fireworks style with more complex animations
const fireworksStyle = `
  @keyframes firework-fade {
    0% { opacity: 1; }
    60% { opacity: 0.8; }
    100% { opacity: 0; }
  }
  
  @keyframes trail-fade {
    0% { opacity: 0.8; }
    40% { opacity: 0.6; }
    100% { opacity: 0; }
  }
  
  @keyframes secondary-fade {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 0; }
  }
  
  ${[...Array(150)]
    .map(
      (_, i) => `
    @keyframes firework-particle-${i} {
      0% {
        transform: rotate(${i * (360 / 150)}deg) translateY(0) scale(1);
      }
      50% {
        transform: rotate(${i * (360 / 150)}deg) translateY(${200 + Math.random() * 100}px) scale(0.8);
      }
      100% {
        transform: rotate(${i * (360 / 150)}deg) translateY(${400 + Math.random() * 200}px) scale(0.4);
      }
    }
  `,
    )
    .join("\n")}
  
  ${[...Array(450)]
    .map(
      (_, i) => `
    @keyframes firework-trail-${i} {
      0% {
        transform: translateY(0) scale(1) rotate(${Math.random() * 360}deg);
      }
      50% {
        transform: translateY(${100 + Math.random() * 200}px) scale(0.8) rotate(${Math.random() * 360}deg);
      }
      100% {
        transform: translateY(${300 + Math.random() * 400}px) scale(0.4) rotate(${Math.random() * 360}deg);
      }
    }
  `,
    )
    .join("\n")}
  
  ${[...Array(90)]
    .map(
      (_, i) => `
    @keyframes secondary-burst-${i} {
      0% {
        transform: rotate(${i * (360 / 90)}deg) translateY(0) scale(1);
      }
      100% {
        transform: rotate(${i * (360 / 90)}deg) translateY(${100 + Math.random() * 60}px) scale(0.5);
      }
    }
  `,
    )
    .join("\n")}
`;

const HouseholdSummary = ({ household }) => {
  const renderSummaryText = () => {
    const { members } = household;
    const primaryMember = members?.[0];
    const secondaryMember = members?.[1];
    const hasBothMembers =
      primaryMember?.firstName && secondaryMember?.firstName;

    return (
      <>
        <p className="mb-4 text-gray-800 dark:text-gray-200">
          {primaryMember?.firstName && (
            <>
              {hasBothMembers
                ? `${primaryMember.firstName} and ${secondaryMember.firstName} have been clients since `
                : `${primaryMember.firstName} has been a client since `}
              <span className="font-bold text-gray-900 dark:text-white">March 2019</span>.
              {primaryMember.firstName} has a{" "}
              <span className="font-bold text-gray-900 dark:text-white">
                Charles Schwab IRA, a Roth IRA, and two Inherited IRAs
              </span>
              {hasBothMembers && (
                <>
                  , and they share a{" "}
                  <span className="font-bold text-gray-900 dark:text-white">Joint Non-Qualified account</span>{" "}
                  as well for a total of{" "}
                  <span className="font-bold text-gray-900 dark:text-white">
                    6 accounts under active management
                  </span>
                </>
              )}
            </>
          )}
        </p>
        <p className="mb-4 text-gray-800 dark:text-gray-200">
          {household?.address?.city && household?.address?.state && (
            <>
              The {hasBothMembers ? "couple are" : "client is"} currently
              retired happily in{" "}
              <span className="font-bold text-gray-900 dark:text-white">
                {household.address.city}, {household.address.state}
              </span>
            </>
          )}
          {primaryMember?.hobbies && (
            <>
              {" "}
              where {primaryMember.firstName} enjoys {primaryMember.hobbies}
              {secondaryMember?.hobbies &&
                `, and ${secondaryMember.firstName} enjoys ${secondaryMember.hobbies}`}
              .
            </>
          )}
        </p>
      </>
    );
  };

  return <div className="prose max-w-none dark:prose-invert">{renderSummaryText()}</div>;
};

const FamilyMemberCard = ({
  member,
  onCall,
  onEmail,
  onMessage,
  onProfileClick,
}) => (
  <div className="flex flex-col items-center group">
    <div className="relative">
      <ActionButton
        icon={<PhoneIcon />}
        onClick={(e) => {
          e.stopPropagation();
          onCall(member);
        }}
        className="-right-5 top-0"
        title="Call"
        hoverColor="text-green-600 dark:text-green-400"
      />
      <ActionButton
        icon={<EmailIcon />}
        onClick={(e) => {
          e.stopPropagation();
          onEmail(member);
        }}
        className="-right-7 top-8"
        title="Email"
        hoverColor="text-blue-600 dark:text-blue-400"
      />
      <ActionButton
        icon={<MessageIcon />}
        onClick={(e) => {
          e.stopPropagation();
          onMessage(member);
        }}
        className="-right-5 top-16"
        title="Message"
        hoverColor="text-purple-600 dark:text-purple-400"
      />
      <div
        className="w-24 h-24 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-full flex items-center justify-center cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-lg"
        onClick={onProfileClick}
        title="Client Profile"
      >
        <span className="text-2xl font-semibold text-blue-700 dark:text-blue-200">
          {member?.firstName?.[0]}
          {member?.lastName?.[0]}
        </span>
      </div>
    </div>
    <div className="mt-4 text-center">
      <div className="font-medium text-gray-900 dark:text-white">
        {member?.firstName} {member?.lastName}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">
        {calculateAge(member?.dateOfBirth)} years old
      </div>
    </div>
  </div>
);

const ActionButton = ({ icon, onClick, className, title, hoverColor }) => (
  <button
    onClick={onClick}
    className={`absolute ${className} p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-150`}
    title={title}
  >
    <div
      className={
        `w-4 h-4 text-gray-600 dark:text-gray-400 transform transition-all duration-150 hover:scale-125 ` +
        (hoverColor ? `hover:${hoverColor} ` : "")
      }
    >
      {icon}
    </div>
  </button>
);

const BankingModal = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} size="sm">
    <div className="p-6 text-center">
      <div className="flex justify-end">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      <div className="mb-4 flex justify-center">
        <div className="w-12 h-12 rounded-full bg-red-100/10 flex items-center justify-center">
          <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
      </div>
      <h3 className="text-lg font-medium mb-2">Access Requires Banking Integration</h3>
      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Please integrate banking information to access this feature.</p>
      <button
        onClick={onClose}
        className="text-gray-600 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-300"
      >
        Close
      </button>
    </div>
  </Modal>
);

const HouseholdProfile = () => {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAnnualReviewDropdown, setShowAnnualReviewDropdown] =
    useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showReassignConfirmModal, setShowReassignConfirmModal] =
    useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showBankingPopup, setShowBankingPopup] = useState(false);
  const [showQCoinPopup, setShowQCoinPopup] = useState(false);
  const [investmentRewardCollected, setInvestmentRewardCollected] =
    useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [annualReviewVersions, setAnnualReviewVersions] = useState({});
  const [selectedAdvisor, setSelectedAdvisor] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [retainSubAdvisor, setRetainSubAdvisor] = useState(false);
  const [roles, setRoles] = useState({
    isPrimaryAdvisor: false,
    isSubAdvisor: false,
  });
  const [advisors, setAdvisors] = useState([]);
  const [suggestions, setSuggestions] = useState([
    {
      text: "Rebalance portfolio to reduce risk 5 points for 100% portfolio alignment",
      tag: { text: "Urgent", color: "red" },
      isChecked: false,
    },
    {
      text: "Schedule annual portfolio review for Q3 planning",
      tag: { text: "Deadline Upcoming", color: "yellow" },
      isChecked: false,
    },
    {
      text: "Contact client regarding RMD for IRA account",
      tag: { text: "Recommended", color: "blue" },
      isChecked: false,
    },
  ]);
  const dropdownRef = useRef(null);
  const settingsRef = useRef(null);
  const [showCommModal, setShowCommModal] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [loadingAISuggestions, setLoadingAISuggestions] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const { theme } = useTheme();

  const navigationItems = [
    {
      label: "Communications",
      path: `/advisor-portal/client-database/household-profile/${householdId}/communications`,
      icon: "/images/AdvisorPortal/communications.png",
    },
    {
      label: "Documents",
      path: `/advisor-portal/client-database/household-profile/${householdId}/documents`,
      icon: "/images/AdvisorPortal/documents.png",
    },
    {
      label: "Calculators",
      path: `/advisor-portal/client-database/household-profile/${householdId}/calculators`,
      icon: "/images/AdvisorPortal/calculator.png",
    },
    {
      label: "Relationship",
      path: `/advisor-portal/client-database/household-profile/${householdId}/relationship`,
      icon: "/images/AdvisorPortal/relationship.png",
    },
    {
      label: "Analysis",
      path: `/advisor-portal/client-database/household-profile/${householdId}/analysis`,
      icon: "/images/AdvisorPortal/analysis.png",
    },
    {
      label: "Investments",
      path: `/advisor-portal/client-database/household-profile/${householdId}/investments`,
      icon: "/images/AdvisorPortal/investments.png",
    },
    {
      label: "Tax Planning",
      path: `/advisor-portal/client-database/household-profile/${householdId}/tax-planning`,
      icon: "/images/AdvisorPortal/tax-planning.png",
    },
    {
      label: "Banking",
      path: `/advisor-portal/client-database/household-profile/${householdId}/banking`,
      icon: "/images/AdvisorPortal/banking.png",
    },
    {
      label: "Estate Planning",
      path: `/advisor-portal/client-database/household-profile/${householdId}/estate-planning`,
      icon: "/images/AdvisorPortal/estate-planning.png",
    },
    {
      label: "Insurance",
      path: `/advisor-portal/client-database/household-profile/${householdId}/insurance`,
      icon: "/images/AdvisorPortal/insurance.png",
    },
  ];

  // Calculate specific dates for the household summary
  const getClientSinceDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 5);
    date.setMonth(date.getMonth() - 3);
    date.setDate(date.getDate() - 3);
    return date;
  };

  const getLastContactedDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date;
  };

  // Fetch household data
  useEffect(() => {
    const fetchHouseholdData = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}`,
        );

        // Transform and validate the data
        const primaryMember = response.data.members?.[0];
        const primaryContact = primaryMember?.contact;

        // Detailed logging of the address-related data
        logger.info("Raw API Response:", response.data);
        logger.info("Primary Member:", primaryMember);
        logger.info("Primary Member Contact:", primaryContact);

        // Create address object from all possible sources
        const memberAddress = primaryContact
          ? {
              address_line1:
                primaryContact.address_line1 || primaryContact.addressLine1,
              address_line2:
                primaryContact.address_line2 || primaryContact.addressLine2,
              city: primaryContact.city,
              state: primaryContact.state,
              zip: primaryContact.zip || primaryContact.zipCode,
            }
          : null;

        const householdAddress =
          response.data.address ||
          response.data.Household?.Address ||
          response.data.household?.address;

        // Use the first available address source
        const address = memberAddress ||
          householdAddress || {
            address_line1: undefined,
            address_line2: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
          };

        logger.info("Member Address:", memberAddress);
        logger.info("Household Address:", householdAddress);
        logger.info("Final Address:", address);

        const transformedData = {
          ...response.data,
          householdName:
            response.data.householdName || primaryMember?.lastName || "Unknown",
          members:
            response.data.members?.map((member) => ({
              ...member,
              contact: member.contact || {},
            })) || [],
          address,
        };

        logger.info("Final Transformed Data:", transformedData);
        setHousehold(transformedData);
        setRoles({
          isPrimaryAdvisor: response.data.isPrimaryAdvisor,
          isSubAdvisor: response.data.isSubAdvisor,
        });
      } catch (err) {
        logger.error("Error fetching household data:", err);
        setError(err.message || "Failed to fetch household data");
        setHousehold({ householdName: "Unknown", members: [] });
      } finally {
        setLoading(false);
      }
    };
    if (householdId) fetchHouseholdData();
  }, [householdId]);

  // Fetch advisors
  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        const response = await api.get(
          "/advisor-portal/client-database/advisors",
        );
        setAdvisors(response.data);
      } catch (err) {
        logger.error("Failed to fetch advisors:", err);
        setError("Failed to load advisors");
      }
    };
    fetchAdvisors();
  }, []);

  // Fetch annual review versions
  useEffect(() => {
    const fetchAnnualReviewVersions = async () => {
      try {
        setLoadingVersions(true);
        logger.info("Fetching versions for household:", householdId);
        const response = await api.get(
          `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/versions`,
        );
        logger.info("Versions response:", response.data);
        if (response.data && response.data.versions) {
          setAnnualReviewVersions(response.data.versions);
        } else {
          logger.warn("No versions data in response:", response.data);
          setAnnualReviewVersions({});
        }
      } catch (error) {
        logger.error("Failed to fetch annual review versions:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url,
        });
        setAnnualReviewVersions({});
      } finally {
        setLoadingVersions(false);
      }
    };
    if (showAnnualReviewDropdown) fetchAnnualReviewVersions();
  }, [showAnnualReviewDropdown, householdId]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setShowAnnualReviewDropdown(false);
      if (settingsRef.current && !settingsRef.current.contains(event.target))
        setShowSettingsMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Event handlers
  const handleViewAnnualReview = (year, version) => {
    navigate(
      `/advisor-portal/client-database/household-profile/${householdId}/ar-profile/${year}/${version}`,
    );
  };

  const handleRemoveClick = () => {
    setShowRemoveModal(true);
    setShowSettingsMenu(false);
  };

  const handleReassignClick = () => {
    setShowReassignModal(true);
    setShowSettingsMenu(false);
  };

  const handleReassignProceed = () => {
    if (!selectedAdvisor || !selectedRole) return;
    setShowReassignModal(false);
    setShowReassignConfirmModal(true);
  };

  const handleReassignConfirm = async () => {
    try {
      await api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/reassign`,
        {
          advisorId: selectedAdvisor,
          role: selectedRole,
          retainSubAdvisor,
        },
      );
      setShowReassignConfirmModal(false);
      setShowSuccessModal(true);
      setTimeout(() => navigate("/advisor-portal/clients"), 2000);
    } catch (err) {
      logger.error("Failed to reassign household:", err);
      setError(err.message || "Failed to reassign household");
    }
  };

  const handleUpdateRelationships = async () => {
    try {
      const response = await api.put(
        `/advisor-portal/client-database/household-profile/${householdId}/relationships`,
        {
          isPrimaryAdvisor: roles.isPrimaryAdvisor,
          isSubAdvisor: roles.isSubAdvisor,
        },
      );
      setHousehold(response.data);
      setShowRemoveModal(false);
      setShowSuccessModal(true);
      setShowFireworks(true); // Trigger fireworks on success
      setTimeout(() => setShowFireworks(false), 3000);
    } catch (err) {
      logger.error("Failed to update relationships:", err);
      setError(err.message || "Failed to update relationships");
    }
  };

  const handleSuggestionCheck = (index) => {
    const updatedSuggestions = [...suggestions];
    updatedSuggestions[index].isChecked = !updatedSuggestions[index].isChecked;
    setSuggestions(updatedSuggestions);
  };

  const generateNewSuggestions = () => {
    setLoadingAISuggestions(true);
    // Simulate API delay
    setTimeout(() => {
      const newSuggestions = [
        {
          text: "Review tax efficiency of current asset location strategy",
          tag: { text: "Recommended", color: "blue" },
          isChecked: false,
        },
        {
          text: "Introduce new ESG investment options to align with client values",
          tag: { text: "Recommended", color: "blue" },
          isChecked: false,
        },
        {
          text: "Send email about upcoming tax loss harvesting opportunity",
          tag: { text: "Deadline Upcoming", color: "yellow" },
          isChecked: false,
        },
      ];
      setSuggestions(newSuggestions);
      setLoadingAISuggestions(false);
    }, 1500);
  };

  // Communication handlers
  const handleCall = (member) => {
    const phoneNumber =
      member?.contact?.primary_number || member?.contact?.phone;
    if (!phoneNumber) {
      alert("No phone number available for this client");
      return;
    }
    setSelectedMember(member);
    setShowCallModal(true);
  };

  const handleEmail = (member) => {
    const email = member?.contact?.email;
    if (!email) {
      alert("No email address available for this client");
      return;
    }
    setSelectedMember(member);
    setShowEmailModal(true);
  };

  const handleMessage = (member) => {
    const phoneNumber =
      member?.contact?.primary_number || member?.contact?.phone;
    if (!phoneNumber) {
      alert("No phone number available for this client");
      return;
    }
    setSelectedMember(member);
    setShowMessageModal(true);
  };

  const handleSendMessage = (messageData) => {
    logger.info("Sending message:", messageData);
    // TODO: Implement message sending functionality
    setShowMessageModal(false);
  };

  const handleConfirmCall = () => {
    if (selectedMember?.contact?.phone) {
      window.location.href = `tel:${selectedMember.contact.phone}`;
    }
    setShowCallModal(false);
  };

  const handleSendEmail = (emailData) => {
    logger.info("Sending email:", emailData);
    // TODO: Implement email sending functionality
    setShowEmailModal(false);
  };

  const handleScheduleMeeting = (member) => {
    setSelectedMember(member);
    setShowScheduleModal(true);
  };

  if (loading)
    return (
      <SubPageLayout
        title="Loading..."
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Client Database", path: "/advisor-portal/client-database" },
          {
            label: "Household Profile",
            path: `/advisor-portal/client-database/household-profile/${householdId}`,
          },
        ]}
      >
        <div className="text-gray-700">Loading...</div>
      </SubPageLayout>
    );

  if (error)
    return (
      <SubPageLayout
        title="Error"
        breadcrumbItems={[
          { label: "Advisor Portal", path: "/advisor-portal" },
          { label: "Client Database", path: "/advisor-portal/client-database" },
          {
            label: "Household Profile",
            path: `/advisor-portal/client-database/household-profile/${householdId}`,
          },
        ]}
      >
        <div className="text-red-500">Error: {error}</div>
      </SubPageLayout>
    );

  const householdLastName =
    household?.householdName?.split(" ").pop() || "Household";

  return (
    <SubPageLayout
      title={
        <div
          className="flex items-center"
          style={{ width: "800px", margin: "0 auto" }}
        >{`${household?.householdName || "Household"} Household Overview`}</div>
      }
      subtitle="View and manage household details"
      breadcrumbItems={[
        { label: "Advisor Portal", path: "/advisor-portal" },
        { label: "Client Database", path: "/advisor-portal/client-database" },
        {
          label: "Household Profile",
          path: `/advisor-portal/client-database/household-profile/${householdId}`,
        },
      ]}
      actionButton={
        <HeaderActionButtons
          showAnnualReviewDropdown={showAnnualReviewDropdown}
          setShowAnnualReviewDropdown={setShowAnnualReviewDropdown}
          showSettingsMenu={showSettingsMenu}
          setShowSettingsMenu={setShowSettingsMenu}
          loadingVersions={loadingVersions}
          annualReviewVersions={annualReviewVersions}
          handleViewAnnualReview={handleViewAnnualReview}
          handleReassignClick={handleReassignClick}
          handleRemoveClick={handleRemoveClick}
          dropdownRef={dropdownRef}
          settingsRef={settingsRef}
        />
      }
    >
      <style>{fireworksStyle}</style>
      <div className="grid grid-rows-[auto,1fr,auto] gap-6">
        <div className="grid grid-cols-2 gap-6">
          <div
            className={`${cardClass} ${hoverUpClass} bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-800 dark:text-white`}
          >
            <div className={cardHeaderClass}>
              <svg
                className="w-5 h-5 text-blue-600 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              <h3 className={cardTitleClass}>Household Summary</h3>
            </div>

            <HouseholdSummary household={household} />

            <div className="flex justify-end mt-4">
              <div className="text-blue-700 dark:text-blue-400 text-sm font-medium">
                Last contacted {formatDate(getLastContactedDate())}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-5 grid-rows-2 gap-4 h-full">
            {navigationItems.map((item, index) => {
              const gradientColors = calculateGradient(
                iconProgress[item.label].available,
                iconProgress[item.label].total,
              );
              return (
                <div
                  key={index}
                  className={`${cardClass} bg-gradient-to-br ${gradientColors.background} p-4 hover:scale-[1.03] hover:shadow-xl active:scale-95 transition-all duration-200 cursor-pointer group rounded-xl relative flex flex-col items-center justify-center min-h-[120px] overflow-hidden`}
                  onClick={() => {
                    if (item.label === "Banking") {
                      setShowBankingPopup(true);
                      return;
                    }
                    if (
                      item.label === "Investments" &&
                      !investmentRewardCollected
                    ) {
                      setShowQCoinPopup(true);
                      return;
                    }
                    navigate(item.path);
                  }}
                >
                  {item.label === "Banking" && (
                    <div className="absolute top-2 right-2 z-20">
                      <svg
                        className="w-4 h-4 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                    </div>
                  )}
                  {item.label === "Investments" &&
                    !investmentRewardCollected && (
                      <div className="absolute top-2 right-2 z-20 cursor-pointer transform transition-all duration-300 hover:scale-110">
                        <img
                          src="/images/advisor-portal/icons/q-icon.png"
                          alt="Q-Coin"
                          className="w-6 h-6"
                        />
                      </div>
                    )}
                  <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                  <div className="relative w-full h-full flex flex-col items-center justify-center">
                    {item.label === "Calculators" && (
                      <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-md py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                        32/46 Available
                      </div>
                    )}
                    <div className="w-10 h-10 mb-3 transition-all duration-200 group-hover:scale-110 relative z-10 flex items-center justify-center">
                      <img
                        src={item.icon}
                        alt={item.label}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-800 text-center block transition-colors duration-200 group-hover:text-blue-700 relative z-10 mt-1 font-inter">
                      {item.label}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gray-100">
                    <div
                      className={`h-full transition-all duration-200 ${gradientColors.progress}`}
                      style={{
                        width: `${(iconProgress[item.label].available / iconProgress[item.label].total) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div
            className={`${cardClass} ${hoverUpClass} bg-gradient-to-b from-blue-50 to-white dark:from-gray-800 dark:to-gray-800 dark:text-white relative`}
          >
            <a
              href="/advisor-portal/calendar-events"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleScheduleMeeting(household?.members?.[0]);
              }}
              className="absolute top-4 right-4 text-teal-300 hover:text-teal-400 text-sm font-medium transition-colors duration-200"
            >
              Schedule Meeting
            </a>
            <div className={cardHeaderClass}>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className={cardTitleClass}>Family Members</h3>
              </div>
            </div>

            <div className="flex flex-col items-center relative">
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ zIndex: 0 }}
              >
                {household?.members?.length > 2 &&
                  household.members.slice(2).map((_, index) => (
                    <g key={`connector-${index}`}>
                      <path
                        d={`M ${50 + index * 120} 180 L ${50 + index * 120} 240`}
                        stroke="#E5E7EB"
                        strokeWidth="2"
                      />
                    </g>
                  ))}
                {household?.members?.length > 2 && (
                  <path
                    d={`M ${50} 180 L ${50 + (household.members.length - 3) * 120} 180`}
                    stroke="#E5E7EB"
                    strokeWidth="2"
                  />
                )}
              </svg>
              <div className="flex justify-center space-x-16 mb-16 relative z-10">
                {household?.members?.slice(0, 2).map((member, index) => (
                  <FamilyMemberCard
                    key={index}
                    member={member}
                    onCall={handleCall}
                    onEmail={handleEmail}
                    onMessage={handleMessage}
                    onProfileClick={() =>
                      navigate(
                        `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${member.userId || member.user_id}/household-information`,
                      )
                    }
                  />
                ))}
              </div>
              {household?.members?.length > 2 && (
                <div className="flex justify-center space-x-8 mt-8">
                  {household.members.slice(2).map((member, index) => (
                    <FamilyMemberCard
                      key={index}
                      member={member}
                      onCall={handleCall}
                      onEmail={handleEmail}
                      onMessage={handleMessage}
                      onProfileClick={() =>
                        navigate(
                          `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${member.userId || member.user_id}/household-information`,
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <RiskProfileCard householdId={householdId} />
            <FinancialHealthCard healthScore={85} />
          </div>
        </div>
        <TaskRecommendations
          suggestions={suggestions}
          onSuggestionCheck={handleSuggestionCheck}
          onGenerateNew={generateNewSuggestions}
          loading={loadingAISuggestions}
        />
      </div>

      {/* Modals with higher z-index */}
      {showRemoveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-lg shadow-xl relative max-w-md w-full mx-4">
            <button
              onClick={() => setShowRemoveModal(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Update Household Relationships
            </h2>
            <div className="mb-4">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="primaryAdvisorCheckbox"
                  checked={roles.isPrimaryAdvisor}
                  onChange={(e) =>
                    setRoles((prev) => ({
                      ...prev,
                      isPrimaryAdvisor: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <label
                  htmlFor="primaryAdvisorCheckbox"
                  className="text-gray-700 font-medium"
                >
                  Primary Advisor
                </label>
              </div>
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="subAdvisorCheckbox"
                  checked={roles.isSubAdvisor}
                  onChange={(e) =>
                    setRoles((prev) => ({
                      ...prev,
                      isSubAdvisor: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <label
                  htmlFor="subAdvisorCheckbox"
                  className="text-gray-700 font-medium"
                >
                  Sub-Advisor
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowRemoveModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRelationships}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
      {showReassignModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-lg shadow-xl relative max-w-md w-full mx-4">
            <button
              onClick={() => setShowReassignModal(false)}
              className="absolute top-2 right-2 text-gray-600 hover:text-gray-800"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Reassign Household
            </h2>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Select Advisor:
              </label>
              <select
                value={selectedAdvisor}
                onChange={(e) => setSelectedAdvisor(e.target.value)}
                className="w-full p-2 border rounded text-black"
              >
                <option value="">Select an advisor</option>
                {advisors.map((advisor) => (
                  <option key={advisor.id} value={advisor.id}>
                    {advisor.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Select Role:
              </label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full p-2 border rounded text-black"
              >
                <option value="">Select a role</option>
                <option value="Primary">Primary</option>
                <option value="Sub-Advisor">Sub-Advisor</option>
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowReassignModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignProceed}
                disabled={!selectedAdvisor || !selectedRole}
                className={`px-4 py-2 text-white rounded ${!selectedAdvisor || !selectedRole ? "bg-gray-400 cursor-not-allowed" : "bg-green-500 hover:bg-green-600"}`}
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}
      {showReassignConfirmModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-lg shadow-xl relative max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Are you sure you want to reassign {household?.householdName} as{" "}
              {selectedRole}?
            </h2>
            <p className="text-red-600 mb-4">
              You will lose access to this household. Do you want to retain
              Sub-Advisor access?
            </p>
            <div className="flex items-center mb-6">
              <input
                type="checkbox"
                id="retainSubAdvisor"
                checked={retainSubAdvisor}
                onChange={(e) => setRetainSubAdvisor(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="retainSubAdvisor" className="text-gray-700">
                Retain Sub-Advisor role
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowReassignConfirmModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleReassignConfirm}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-lg shadow-xl relative max-w-md w-full mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-xl font-bold mb-4 text-center text-gray-800">
              Operation Successful
            </h2>
            <p className="text-gray-600 text-center mb-6">
              The changes have been applied successfully.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showBankingPopup && <BankingModal isOpen={showBankingPopup} onClose={() => setShowBankingPopup(false)} />}
      {showQCoinPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-8 relative shadow-xl dark:shadow-black/50">
            <button
              onClick={() => setShowQCoinPopup(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex flex-col items-center">
              <div className="w-24 h-24 mb-6 animate-bounce">
                <img
                  src="/images/advisor-portal/icons/q-icon.png"
                  alt="Q-Coin"
                  className="w-full h-full object-contain"
                />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white text-center mb-4">
                Congratulations!
              </h3>

              <p className="text-gray-600 dark:text-gray-300 text-center text-md mb-6">
                A Q-Token (Qt) has been earned for fully integrating the{" "}
                {household?.householdName} Household with Investments.
              </p>

              <button
                onClick={() => {
                  setShowQCoinPopup(false);
                  setInvestmentRewardCollected(true);
                  setShowFireworks(true);
                  setTimeout(() => setShowFireworks(false), 5000);
                }}
                className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 dark:from-yellow-500 dark:to-yellow-600 text-white rounded-lg hover:from-yellow-500 hover:to-yellow-600 dark:hover:from-yellow-600 dark:hover:to-yellow-700 transform hover:scale-105 transition-all duration-200 shadow-md dark:shadow-yellow-500/20"
              >
                Collect Reward
              </button>
            </div>
          </div>
        </div>
      )}
      {showFireworks && (
        <div className="fixed inset-0 pointer-events-none z-[9999]">
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Main burst particles */}
            {[...Array(150)].map((_, i) => (
              <div
                key={`main-${i}`}
                className="absolute w-4 h-4 rounded-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 opacity-0"
                style={{
                  animation: `
                    firework-particle-${i} 2s cubic-bezier(0.22, 0.61, 0.36, 1) forwards,
                    firework-fade 2s cubic-bezier(0.22, 0.61, 0.36, 1) forwards
                  `,
                  animationDelay: `${Math.random() * 0.5}s`,
                }}
              />
            ))}
            {/* Cascading trail particles */}
            {[...Array(450)].map((_, i) => (
              <div
                key={`trail-${i}`}
                className="absolute w-2 h-2 rounded-full bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-100 opacity-0"
                style={{
                  animation: `
                    firework-trail-${i} 2s cubic-bezier(0.22, 0.61, 0.36, 1) forwards,
                    trail-fade 2s cubic-bezier(0.22, 0.61, 0.36, 1) forwards
                  `,
                  animationDelay: `${Math.random() * 1}s`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
              />
            ))}
            {/* Secondary bursts */}
            {[...Array(9)].map((_, burstIndex) => (
              <div
                key={`burst-${burstIndex}`}
                className="absolute"
                style={{
                  left: `${10 + burstIndex * 10}%`,
                  top: `${20 + Math.random() * 60}%`,
                }}
              >
                {[...Array(90)].map((_, i) => (
                  <div
                    key={`secondary-${burstIndex}-${i}`}
                    className="absolute w-3 h-3 rounded-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 opacity-0"
                    style={{
                      animation: `
                        secondary-burst-${i} 1.5s cubic-bezier(0.22, 0.61, 0.36, 1) forwards,
                        secondary-fade 1.5s cubic-bezier(0.22, 0.61, 0.36, 1) forwards
                      `,
                      animationDelay: `${0.5 + burstIndex * 0.2}s`,
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <CallConfirmationModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        client={selectedMember}
        onConfirm={handleConfirmCall}
      />
      <EmailComposerModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        client={selectedMember}
        onSend={handleSendEmail}
      />
      <NewMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        client={selectedMember}
        onSend={handleSendMessage}
      />
      <ScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        client={selectedMember}
        onSchedule={(scheduleData) => {
          logger.info("Scheduling meeting:", scheduleData);
          setShowScheduleModal(false);
        }}
      />
    </SubPageLayout>
  );
};

export default HouseholdProfile;
